import Media from 'components/shared/Media/Media';
import { cn } from 'utils/styles';
import { BackgroundColor } from 'types/constants';
import RichTextHeadline from 'components/shared/RichTextHeadline/RichTextHeadline';
import { ArticleGridProps, RelatedArticleProps } from 'libs/contentful/props';
import Link from 'components/shared/Link/Link';
import { getRichTextValue } from 'utils/formatters';

export enum FeaturedArticleColor {
  Blue = 'Blue',
  Pink = 'Pink',
  Yellow = 'Yellow',
  Purple = 'Purple',
  Tertiary = 'Tertiary (Medium Green)',
  Inverse = 'Inverse (Dark Green)',
}

const getArticleBackgroundColor = (articleColor?: FeaturedArticleColor) => {
  // Still use white colorway because it applies default colors for text and buttons
  switch (articleColor) {
    case FeaturedArticleColor.Blue:
      return `${BackgroundColor.White} bg-security-200`;
    case FeaturedArticleColor.Pink:
      return `${BackgroundColor.White} bg-ease-200`;
    case FeaturedArticleColor.Purple:
      return `${BackgroundColor.White} bg-aspiration-200`;
    case FeaturedArticleColor.Yellow:
      return `${BackgroundColor.White} bg-freedom-200`;
    case FeaturedArticleColor.Inverse:
      return BackgroundColor.DarkGreen;
    case FeaturedArticleColor.Tertiary:
    default:
      return BackgroundColor.MediumGreen;
  }
};

export interface FeaturedArticleProps extends RelatedArticleProps, Pick<ArticleGridProps, 'featuredArticleColor'> {}

const FeaturedArticle = ({
  articleImage,
  categories,
  category,
  articleTitle,
  featuredArticleColor,
  slug,
}: FeaturedArticleProps) => {
  const categoryTitle = categories?.items?.[0]?.id || category?.label;

  const linkText = 'Read more';
  const plainTitle = getRichTextValue(articleTitle);
  // TODO: incorporate alternateTitle and alternateImage from Page: Blog to
  // override the article and image used in this component
  return (
    <div
      data-testid={'featuredArticle'}
      className={cn(
        'flex sm:flex-row flex-col-reverse sm:max-h-105 h-full sm:max-w-[870px] gap-y-6 rounded-lg',
        getArticleBackgroundColor(featuredArticleColor),
      )}>
      <div className="flex flex-col grow justify-between sm:basis-1/2 space-y-2.5 p-6 sm:p-8 max-sm:h-60 ">
        <div className="space-y-4">
          <div className="font-serif text-eyebrow font-normal capitalize">{categoryTitle}</div>
          <div className="font-sans sm:text-heading-md text-heading-sm">
            <Link internalPage={{ slug }} className="sm:text-heading-md text-heading-sm">
              <RichTextHeadline headline={articleTitle} />
            </Link>
          </div>
        </div>
        <div className="font-sans text-label-saans underline font-medium">
          <Link internalPage={{ slug }} className="underline" aria-label={`${linkText}: ${plainTitle}`}>
            {linkText}
          </Link>
        </div>
      </div>
      {articleImage && (
        <div className="basis-1/2 max-sm:h-[220px] w-auto sm:max-w-[438px]">
          <Media
            media={articleImage}
            sizes={'(max-width: 750px) 100vw, 438px'}
            className={'h-full w-full max-sm:rounded-b-none sm:rounded-l-none object-cover'}
          />
        </div>
      )}
    </div>
  );
};

export default FeaturedArticle;
